import React, { useState, useEffect } from "react";
import { Grid, Backdrop, Typography, Button, Box, styled, useTheme } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSpring, animated, config as springConfig } from "react-spring";

import * as ROUTES from "../../constants/routes";

import { ReactComponent as Coin } from "./Icons/coin.svg";

import { clickWellDone } from "../../actions/chat_actions";
import { fetchUser, doneProfileEdit } from "../../actions/user_actions";
import { getMissionsAndInterventions } from "../../actions/mission_actions";

// Use styled utility for complex, animated styles
const StyledCoinDiv = styled(animated.div)(({ theme }) => ({
  width: 155,
  height: 181,
  marginLeft: "auto",
  marginRight: "auto",
}));

const WellDone = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();

  const [show, setShow] = useState(false);
  const { isInterventionOngoing, isProfileBeingEdited } = useSelector(state => state.user);
  const { showWellDone = false, showChat } = useSelector(state => state.chat);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  const animatedProps = useSpring({
    transform: showWellDone ? "scale(1, 1)" : "scale(-0.1, 0.1)",
    opacity: showWellDone ? 1 : 0.5,
    delay: showWellDone ? 200 : 0,
    config: springConfig.wobbly,
  });

  // Handles the main timed sequence of events
  useEffect(() => {
    let timeout1, timeout2, timeout3;
    
    if (showWellDone) {
      timeout1 = setTimeout(() => {
        if (!isInterventionOngoing && !isProfileBeingEdited) {
          setShow(true);
        }
      }, 4000);

      timeout2 = setTimeout(async () => {
        let runDoneEdit = false;
        if (isInterventionOngoing) {
          navigate(ROUTES.MISSIONS);
          runDoneEdit = true;
        }
        if (isProfileBeingEdited) {
          navigate(ROUTES.PROFILE);
          runDoneEdit = true;
        }
        await dispatch(fetchUser());
        if (runDoneEdit) {
          dispatch(doneProfileEdit());
        }
        await dispatch(getMissionsAndInterventions(true));
      }, 5000);

      timeout3 = setTimeout(() => {
        if (!isInterventionOngoing && !isProfileBeingEdited) {
          navigate(ROUTES.JOBS);
        } else {
          dispatch(clickWellDone());
        }
      }, 6000);
    }

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [showWellDone]);

  // When the backdrop should close
  useEffect(() => {
    if (!showWellDone) {
      setShow(false);
    }
  }, [showWellDone]);

  if(show){
    return (
      <Backdrop
        open={show}
        transitionDuration={{
          appear: 0,
          exit: 500,
          enter: 200,
        }}
        sx={{
          zIndex: 8000,
        }}
      >
        <Box sx={{
          flexGrow: 1,
          zIndex: 12000,
          position: "absolute",
          backgroundColor: "#53D28E",
          width: "100%",
          height: "100%",
          overflow: 'scroll',
        }}>
          <Grid
            container
            spacing={0}
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "100%" }}
          >
            <Grid item sx={{ textAlign: "center" }}>
              <StyledCoinDiv style={animatedProps}>
                <Coin sx={{ width: "100%", height: "100%" }} />
              </StyledCoinDiv>

              <Typography variant="h4" sx={{
                fontSize: 30,
                fontWeight: "bold",
                color: "#ffffff",
                mt: 2,
                mb: 1.875,
              }}>
                Well Done!
              </Typography>
              <Typography variant="h5" sx={{
                fontSize: 22,
                fontWeight: "bold",
                color: "#ffffff",
                mt: 2,
                mb: 1,
              }}>
                You’ve completed the initial assessment.
              </Typography>
              <Typography variant="body1" sx={{
                mx: { xs: 2, sm: 8 },
                textAlign: "center",
                fontSize: { xs: 16, sm: 20 },
                color: "#ffffff",
                mb: 4.5,
              }}>
                We now understand you a lot better – and your opportunities are waiting for you.
                <br />
                But we’d like to get to know you even more. Take part in our missions and we’ll assess your answers further to match you to vacancies perfect for you.
                <br />
                Use the filters to sort and find new jobs, watch videos to see if a new career is right for you and find learning opportunities to fill your skills gaps.
              </Typography>

              <Button
                variant="contained"
                sx={{ px: 3.75 }}
                color="primary"
                disabled={showChat}
                onClick={() => {
                  dispatch(clickWellDone());
                }}
              >
                Reveal Opportunities
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Backdrop>
    );
  }
  else{
    return null
  }
};

export default WellDone;